import { Box, Stack, useTheme } from "@mui/material";
import type { SxProps, Theme, TypographyVariants } from "@mui/material/styles";
import { Button, Lightbox } from "components";
import DOMPurify from "dompurify";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";

const COLLAPSE_THRESHOLD = 1000;

const ALLOWED_TAGS = [
  "p",
  "br",
  "strong",
  "b",
  "em",
  "i",
  "u",
  "s",
  "strike",
  "del",
  "a",
  "ul",
  "ol",
  "li",
  "blockquote",
  "pre",
  "code",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "img",
  "span",
  "div",
  "table",
  "thead",
  "tbody",
  "tr",
  "th",
  "td",
  "hr",
  "sub",
  "sup",
  "mark",
];

const ALLOWED_ATTR = [
  "href",
  "target",
  "rel",
  "src",
  "alt",
  "width",
  "height",
  "style",
  "class",
  "data-type",
  "data-id",
  "data-label",
  "loading",
];

const EMAIL_HTML_PATTERN = /<!doctype\s|<html[\s>]/i;

function sanitize(html: string): string {
  const isEmail = EMAIL_HTML_PATTERN.test(html);

  let cleaned = html;

  if (isEmail) {
    cleaned = cleaned
      .replace(/<!doctype[^>]*>/gi, "")
      .replace(/<html[^>]*>|<\/html>/gi, "")
      .replace(/<head[\s\S]*?<\/head>/gi, "")
      .replace(/<body[^>]*>|<\/body>/gi, "")
      .replace(/<style[\s\S]*?<\/style>/gi, "")
      .replace(/<!--[\s\S]*?-->/g, "")
      .replace(/>\s+</g, "><")
      .replace(/&nbsp;/g, " ")
      .replace(/<p>&nbsp;<\/p>/gi, "")
      .replace(/<p>\s*<\/p>/gi, "")
      .trim();
  } else {
    // Preserve empty paragraphs as line breaks (TipTap blank lines)
    cleaned = cleaned
      .replace(/<p>&nbsp;<\/p>/gi, "<br>")
      .replace(/<p>\s*<\/p>/gi, "<br>");
  }

  cleaned = cleaned.replace(/\n{3,}/g, "\n\n").replace(/\n/g, "<br>");

  return DOMPurify.sanitize(cleaned, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    ADD_ATTR: ["target"],
  });
}

/** Enforce target="_blank" + rel on all links after sanitization */
function patchLinks(html: string): string {
  return html.replace(
    /<a\s/gi,
    '<a target="_blank" rel="noopener noreferrer" ',
  );
}

/** Enforce max-width on images + add placeholder sizing for scroll anchoring */
function patchImages(html: string): string {
  return html.replace(
    /<img\s/gi,
    '<img loading="lazy" style="max-width:100%;height:auto;min-height:150px;aspect-ratio:16/9;object-fit:contain;" ',
  );
}

/** Run the normal message sanitize + link/image patches over a chunk. */
function renderChunk(html: string): string {
  return patchImages(patchLinks(sanitize(html)));
}

// TipTap serializes the HTML-code-block node as an EMPTY <div> whose payload
// lives in a `data-code` attribute:
//   <div data-type="html-code-block" data-code="&lt;h1&gt;…"></div>
// The generic renderer can't show that (empty element + attr is stripped by
// sanitize), so we detect it up-front and only then switch to segmented render.
const HTML_CODE_BLOCK_MARKER = "html-code-block";

type MessageSegment =
  | { id: string; type: "html"; html: string }
  | { id: string; type: "code"; code: string };

/**
 * Split a message's HTML into ordered segments: runs of normal HTML (each
 * sanitized like the default path) interleaved with html-code-block payloads.
 * Only the top-level nodes are inspected — TipTap block nodes always serialize
 * at the document root, so a code block is never nested inside a paragraph.
 */
function parseMessageSegments(html: string): MessageSegment[] {
  const doc = new DOMParser().parseFromString(html, "text/html");
  const segments: MessageSegment[] = [];
  let buffer = "";

  const flush = () => {
    const rendered = renderChunk(buffer);
    if (rendered.trim())
      segments.push({
        id: `seg-${segments.length}`,
        type: "html",
        html: rendered,
      });
    buffer = "";
  };

  for (const node of Array.from(doc.body.childNodes)) {
    const isCodeBlock =
      node.nodeType === Node.ELEMENT_NODE &&
      (node as HTMLElement).getAttribute("data-type") === "html-code-block";

    if (isCodeBlock) {
      flush();
      // getAttribute already HTML-unescapes the value (&lt; -> <).
      const code = (node as HTMLElement).getAttribute("data-code") ?? "";
      segments.push({ id: `seg-${segments.length}`, type: "code", code });
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      buffer += (node as HTMLElement).outerHTML;
    } else {
      buffer += node.textContent ?? "";
    }
  }
  flush();

  return segments;
}

/**
 * Read-only, sandboxed preview of an html-code-block's HTML. Rendered in an
 * iframe with `sandbox="allow-same-origin"` (NO allow-scripts): styles apply and
 * CSS is scoped away from the app, scripts never execute, and we can still read
 * `contentDocument.scrollHeight` to size the frame. Content is DOMPurified too.
 */
const HtmlCodeBlockPreview = memo(function HtmlCodeBlockPreview({
  code,
}: {
  code: string;
}) {
  const theme = useTheme();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const bodyObserverRef = useRef<ResizeObserver | null>(null);
  const scaleRef = useRef(1);
  const [height, setHeight] = useState(0);

  const srcDoc = useMemo(() => {
    const sanitized = DOMPurify.sanitize(code, {
      WHOLE_DOCUMENT: true,
      ADD_TAGS: ["style"],
      ADD_ATTR: ["data-value", "data-label", "data-id", "data-type"],
    });

    // Base defaults injected as the FIRST style in <head> so they're the lowest
    // priority — the content's own inline/`<style>` rules always win. This only
    // resets the raw-browser look (serif font, stray body margin) so previews
    // read cleanly; it never overrides the pasted HTML's styling.
    const baseStyle = `<style>
        html, body { margin: 0; padding: 0; }
        /* Non-interactive preview: hide the iframe's own scrollbars (they can't
           be scrolled anyway) AND stop a scrollbar from stealing layout width,
           which was distorting the fit-to-width measurement into showing both a
           vertical and horizontal bar. */
        html, body { scrollbar-width: none; -ms-overflow-style: none; }
        html::-webkit-scrollbar, body::-webkit-scrollbar { display: none; }
        body {
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
          font-size: 14px;
          line-height: 1.5;
          color: ${theme.palette.lightBg.medium};
          word-break: break-word;
          overflow-wrap: break-word;
        }
        img { max-width: 100%; height: auto; }
        /* Compact, single-direction block spacing so plain semantic HTML (no
           author CSS) doesn't render with big document-style gaps in a chat
           bubble. Lowest priority — content that sets its own margins wins. */
        h1, h2, h3, h4, h5, h6, p, ul, ol, pre, blockquote, figure, table {
          margin: 0 0 0.5em;
        }
        /* Trim the leading/trailing gap at the very top and bottom of the block. */
        body > :first-child { margin-top: 0; }
        body > :last-child { margin-bottom: 0; }
      </style>`;

    if (/<head[^>]*>/i.test(sanitized)) {
      return sanitized.replace(/(<head[^>]*>)/i, `$1${baseStyle}`);
    }
    if (/<html[^>]*>/i.test(sanitized)) {
      return sanitized.replace(
        /(<html[^>]*>)/i,
        `$1<head>${baseStyle}</head>`,
      );
    }
    return `${baseStyle}${sanitized}`;
  }, [code, theme.palette.lightBg.medium]);

  // Fit-to-width: let the content lay out at the width it actually needs, then
  // scale the whole iframe down so it fits the chat column. Narrow content
  // renders 1:1 (scale 1); wide content (fixed/1200px layouts, multi-column
  // grids) is shrunk to fit instead of being clipped or side-scrolled. Height
  // uses max(documentElement, body) scrollHeight so trailing margins count.
  const measure = useCallback(() => {
    const wrapper = wrapperRef.current;
    const iframe = iframeRef.current;
    if (!(wrapper && iframe)) return;
    const doc = iframe.contentDocument;
    if (!doc?.documentElement) return;

    const available = wrapper.clientWidth;
    if (available === 0) return;

    // 1) Measure the width the content needs (reset transform + column width).
    //    Collapse height to 0 first so `scrollHeight` reports the TRUE content
    //    height — `documentElement.scrollHeight` is otherwise clamped to the
    //    iframe's current viewport, which left stale blank space at the bottom.
    iframe.style.transform = "none";
    iframe.style.width = `${available}px`;
    iframe.style.height = "0px";
    const required = Math.max(
      doc.documentElement.scrollWidth,
      doc.body?.scrollWidth ?? 0,
    );

    // 2) Only scale down when the content is genuinely wider than the column.
    const layoutWidth = required > available + 1 ? required : available;
    const scale = available / layoutWidth;
    iframe.style.width = `${layoutWidth}px`;

    const contentHeight = Math.max(
      doc.documentElement.scrollHeight,
      doc.body?.scrollHeight ?? 0,
    );
    iframe.style.height = `${contentHeight}px`;
    iframe.style.transform = scale === 1 ? "none" : `scale(${scale})`;
    iframe.style.transformOrigin = "top left";
    scaleRef.current = scale;

    const displayHeight = Math.round(contentHeight * scale);
    setHeight((prev) => (Math.abs(prev - displayHeight) > 1 ? displayHeight : prev));
  }, []);

  // Height-only re-sync for post-load reflow (fonts/images). Kept separate from
  // `measure` (which mutates iframe width) so the body observer can't feed back
  // into a width-remeasure loop. Critical now that scrollbars are hidden — an
  // under-measured height would silently clip content with no bar to reveal it.
  const syncHeight = useCallback(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    const doc = iframe.contentDocument;
    if (!doc?.documentElement) return;
    // Use `body.scrollHeight` — unlike `documentElement.scrollHeight` it is NOT
    // clamped to the iframe viewport, so it can shrink the height back down when
    // content reflows shorter (no stale blank space).
    const contentHeight = doc.body?.scrollHeight ?? doc.documentElement.scrollHeight;
    iframe.style.height = `${contentHeight}px`;
    const displayHeight = Math.round(contentHeight * scaleRef.current);
    setHeight((prev) => (Math.abs(prev - displayHeight) > 1 ? displayHeight : prev));
  }, []);

  const handleLoad = () => {
    measure();
    // Re-measure next frame to catch late layout (fonts/images settling).
    requestAnimationFrame(measure);
    // Track content-height changes after load without a width-remeasure loop.
    const body = iframeRef.current?.contentDocument?.body;
    if (!body) return;
    bodyObserverRef.current?.disconnect();
    const observer = new ResizeObserver(() => syncHeight());
    observer.observe(body);
    bodyObserverRef.current = observer;
  };

  // Re-fit when the chat column width changes. Observing the WRAPPER (not the
  // iframe body) avoids a feedback loop — our width mutations happen inside the
  // iframe and never resize the wrapper.
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    const observer = new ResizeObserver(() => measure());
    observer.observe(wrapper);
    return () => {
      observer.disconnect();
      bodyObserverRef.current?.disconnect();
    };
  }, [measure]);

  if (!code.trim()) return null;

  return (
    <Box
      ref={wrapperRef}
      sx={{
        width: "100%",
        height: `${height}px`,
        border: `1px solid ${theme.palette.neutral["015"]}`,
        borderRadius: "4px",
        margin: "4px 0",
        backgroundColor: theme.palette.common.white,
        overflow: "hidden",
        // Non-interactive preview — mirrors the editor's code-block view.
        pointerEvents: "none",
      }}
    >
      <Box
        component="iframe"
        ref={iframeRef}
        title="HTML code preview"
        srcDoc={srcDoc}
        sandbox="allow-same-origin"
        onLoad={handleLoad}
        sx={{
          display: "block",
          width: "100%",
          border: "none",
          backgroundColor: theme.palette.common.white,
        }}
      />
    </Box>
  );
});

export const SafeHtmlRenderer = memo(function SafeHtmlRenderer({
  html,
  showMore = true,
  variant = "body-medium",
  sx,
}: {
  html: string;
  showMore?: boolean;
  variant?: keyof TypographyVariants;
  sx?: SxProps<Theme>;
}) {
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedImage, setSelectedImage] = useState<{
    url: string;
    name: string;
  } | null>(null);

  const hasCodeBlock = html.includes(HTML_CODE_BLOCK_MARKER);

  // Default path (no code block): identical behaviour to before.
  const sanitizedHtml = useMemo(
    () => (hasCodeBlock ? "" : renderChunk(html)),
    [html, hasCodeBlock],
  );

  // Code-block path: ordered html/code segments.
  const segments = useMemo(
    () => (hasCodeBlock ? parseMessageSegments(html) : []),
    [html, hasCodeBlock],
  );

  // Remove placeholder sizing once images load (prevents scroll jump).
  // Re-run when content changes to find new images.
  // biome-ignore lint/correctness/useExhaustiveDependencies: content deps trigger re-scan of new DOM images
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const images = container.querySelectorAll("img");
    if (images.length === 0) return;

    const clearPlaceholder = (img: HTMLImageElement) => {
      img.style.minHeight = "0";
      img.style.aspectRatio = "";
    };

    const handleLoad = (e: Event) => {
      clearPlaceholder(e.target as HTMLImageElement);
    };

    const handleError = (e: Event) => {
      const img = e.target as HTMLImageElement;
      clearPlaceholder(img);
      img.style.display = "none";
    };

    for (const img of images) {
      if (img.complete) {
        clearPlaceholder(img);
      } else {
        img.addEventListener("load", handleLoad, { once: true });
        img.addEventListener("error", handleError, { once: true });
      }
    }

    return () => {
      for (const img of images) {
        img.removeEventListener("load", handleLoad);
        img.removeEventListener("error", handleError);
      }
    };
  }, [sanitizedHtml, segments]);

  const plainTextLength = useMemo(() => {
    const strip = (h: string) => h.replace(/<[^>]*>/g, "").trim().length;
    if (!hasCodeBlock) return strip(sanitizedHtml);
    return segments.reduce(
      (sum, seg) => sum + (seg.type === "html" ? strip(seg.html) : 0),
      0,
    );
  }, [hasCodeBlock, sanitizedHtml, segments]);

  const isLongMessage = showMore && plainTextLength > COLLAPSE_THRESHOLD;

  const handleContentClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.tagName === "IMG") {
      const img = target as HTMLImageElement;
      setSelectedImage({ url: img.src, name: img.alt || "Image" });
    }
  };

  const contentSx: SxProps<Theme> = {
    outline: "none",
    padding: 0,
    margin: 0,
    typography: variant,
    color: theme.palette.lightBg.medium,
    height:
      showMore && isLongMessage ? (isExpanded ? "auto" : "250px") : "auto",
    minHeight: 0,
    minWidth: 0,
    maxWidth: "100%",
    overflow: "hidden",
    wordBreak: "break-word" as const,

    // Headings — theme typography instead of browser defaults
    "& h1": theme.typography.h1,
    "& h2": theme.typography.h2,
    "& h3": theme.typography.h3,
    "& h4": theme.typography.h4,
    "& h5": theme.typography.h5,
    "& h6": theme.typography.h6,
    "& h1, & h2, & h3, & h4, & h5, & h6": {
      margin: "12px 0 4px",
      color: theme.palette.lightBg.high,
    },
    "& h1:first-of-type, & h2:first-of-type, & h3:first-of-type, & h4:first-of-type, & h5:first-of-type, & h6:first-of-type":
      {
        marginTop: 0,
      },

    // Paragraph
    "& p": {
      marginTop: 0,
      marginBottom: "2px",
    },

    // Tables (forwarded / HTML emails often use fixed-width layout tables that
    // would otherwise overflow the chat bubble). Force them to fit the column
    // and wrap cell content instead of pushing past the container.
    "& table": {
      maxWidth: "100%",
      tableLayout: "fixed",
      borderCollapse: "collapse",
    },
    "& td, & th": {
      overflowWrap: "break-word",
      wordBreak: "break-word",
    },

    // Images
    "& img": {
      maxWidth: "100%",
      height: "auto",
      cursor: "pointer",
      backgroundColor: theme.palette.neutral["005"],
      borderRadius: "4px",
    },

    // Links
    "& a": {
      color: theme.palette.purple.main,
      cursor: "pointer",
      textDecoration: "underline",
    },

    // Lists
    "& ul, & ol": {
      paddingLeft: "24px",
      margin: "8px 0",
    },

    // Task lists (from TipTap)
    "& ul[data-type='taskList']": {
      listStyle: "none",
      paddingLeft: "4px",
    },

    // Bold
    "& strong, & b": {
      fontWeight: 600,
    },

    // Inline code
    "& code": {
      backgroundColor: theme.palette.neutral["005"],
      borderRadius: "3px",
      padding: "2px 4px",
      fontFamily: "monospace",
    },

    // Code blocks
    "& pre": {
      backgroundColor: theme.palette.neutral["005"],
      borderRadius: "4px",
      padding: "8px 12px",
      overflow: "auto",
      margin: "8px 0",
      "& code": {
        backgroundColor: "transparent",
        padding: 0,
      },
    },

    // Blockquote
    "& blockquote": {
      borderLeft: `3px solid ${theme.palette.neutral["015"]}`,
      paddingLeft: "12px",
      margin: "4px 0",
      color: theme.palette.lightBg.medium,
    },

    // Mention chips (from TipTap input)
    "& .chat-mention, & [data-type='mention']": {
      ...theme.typography["tag-label-xs"],
      backgroundColor: "#D2DDFF",
      color: "#5563F5",
      borderRadius: "4px",
      padding: "1px 6px",
    },

    // Highlight (from TipTap Highlight extension)
    "& mark": {
      borderRadius: "2px",
      padding: "1px 2px",
    },

    // Horizontal rule
    "& hr": {
      border: "none",
      borderTop: `1px solid ${theme.palette.neutral["015"]}`,
      margin: "8px 0",
    },
  };

  return (
    <Stack>
      {hasCodeBlock ? (
        <Box
          ref={containerRef}
          onClick={handleContentClick}
          sx={[contentSx, ...(Array.isArray(sx) ? sx : [sx])]}
        >
          {segments.map((seg) =>
            seg.type === "code" ? (
              <HtmlCodeBlockPreview key={seg.id} code={seg.code} />
            ) : (
              <Box
                key={seg.id}
                // biome-ignore lint/security/noDangerouslySetInnerHtml: sanitized via DOMPurify
                dangerouslySetInnerHTML={{ __html: seg.html }}
              />
            ),
          )}
        </Box>
      ) : (
        <Box
          ref={containerRef}
          onClick={handleContentClick}
          // biome-ignore lint/security/noDangerouslySetInnerHtml: sanitized via DOMPurify
          dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
          sx={[contentSx, ...(Array.isArray(sx) ? sx : [sx])]}
        />
      )}
      {showMore && isLongMessage && (
        <Box>
          <Button
            variant="ghost"
            onClick={() => setIsExpanded((prev) => !prev)}
            sx={{
              p: 0,
              minWidth: "0px",
              display: "block",
              "&:hover": {
                backgroundColor: "transparent",
              },
            }}
          >
            {isExpanded ? "View Less" : "View More"}
          </Button>
        </Box>
      )}
      {selectedImage && (
        <Lightbox
          url={selectedImage.url}
          filename={selectedImage.name}
          open={selectedImage !== null}
          onClose={() => setSelectedImage(null)}
          fileType="image/png"
        />
      )}
    </Stack>
  );
});
